import { FormEditActions } from "@arelia-components/form/FormEditActions";
import ListFormItem from "@arelia-components/form/ListFormItem";
import MenuSelector from "@arelia-components/ui-atoms/menu-selector/MenuSelector";
import { useHandleFormActions } from "@arelia-hooks/formikHooks";
import { Roles } from "@arelia-types/index";
import { Button, Card, CardContent, CardHeader, FormControl, InputLabel, List, MenuItem, Select, TextField, Typography } from "@mui/material";
import { FormikProps } from "formik";
import { ChangeEvent, useMemo, useState } from "react";
import { countryNames } from "src/widgets/countries/constants";
import { formConfig } from "./constants";
import { EditForm } from "./CustomerInfoContainer";
import ResetUser2FADialog from "./ResetUser2FADialog";

type Props = {
  formik: FormikProps<EditForm>;
  initEditing: boolean;
  handeChange: (name: keyof EditForm) => (event: ChangeEvent<HTMLInputElement>) => void;
  onReset2FA: () => void;
  twoFactorEnabled?: boolean;
};

export default function CustomerInfoPresenter({ formik, initEditing, handeChange, onReset2FA, twoFactorEnabled }: Props) {
  const [editing, setEditing] = useState(initEditing);
  const formHandlers = useHandleFormActions<EditForm>(setEditing, formik);
  const roles = useMemo(() => [null, ...Object.values(Roles)], []);
  const [reset2FAOpen, setReset2FAOpen] = useState(false);

  const readFieldRenderer = (field: keyof EditForm) => {
    const value = formik.values[field];
    let label = value;

    if (value === null && field === "initialPassword") {
      label = "Password was already changed";
    }

    if (value === null && field === "role") {
      label = "Customer";
    }

    return <Typography variant="subtitle2">{label}</Typography>;
  }

  const countryRenderer = (field: keyof EditForm) => {
    const value = formik.values[field];

    return <FormControl fullWidth>
      <InputLabel id="country-select">Country</InputLabel>
      <Select
        labelId="country-select"
        value={value}
        label="Country"
        onChange={event => { handeChange(field)(event as ChangeEvent<HTMLInputElement>)}}
        MenuProps={{
          PaperProps: {
            style: {
              maxHeight: 170,
            },
          },
        }}
      >
        {countryNames.map((country, index) => <MenuItem key={index} value={country}>{country}</MenuItem>)}
      </Select>
    </FormControl>
  }

  const editFieldRenderer = (field: keyof EditForm) => {
    const value = formik.values[field];

    if (field === "role") {
      return <MenuSelector<Roles>
        selectedValue={value as Roles}
        onClick={role => handeChange(field)({ target: { value: role } } as any)}
        menuItems={roles.map(x => ({ label: x ?? 'Customer', value: x as Roles }))}
      />
    }

    if (field === "country") {
      return countryRenderer(field)
    }

    return <TextField
      value={value || ""}
      onChange={handeChange(field)}
      disabled={field === "initialPassword"}
      label={value === null && field === "initialPassword" ? "Password was already changed" : ""}
      size="small"
      type="text"
      fullWidth
    />;
  }

  return (
    <Card sx={styles.card}>
      <CardHeader
        sx={styles.cardHeader}
        title="Customer info"
        action={<FormEditActions edit={editing} {...formHandlers} />}
      />
      <CardContent sx={{ justifyContent: "flex-start" }}>
        <List>
          {formConfig.map(({ label, field }, index) => (
            <ListFormItem key={index} label={label}>
              {editing ? editFieldRenderer(field) : readFieldRenderer(field)}
            </ListFormItem>
          ))}

          {twoFactorEnabled && (
            <ListFormItem label="Two-Factor Authentication">
              <Button variant="outlined" color="error" onClick={() => setReset2FAOpen(true)}>
                Reset 2FA
              </Button>
            </ListFormItem>
          )}
        </List>
      </CardContent>

      <ResetUser2FADialog
        open={reset2FAOpen}
        onClose={(confirmed) => {
          setReset2FAOpen(false);
          if (confirmed) onReset2FA();
        }}
      />
    </Card>
  );
}

const styles = {
  card: {
    gridColumn: "span 8",
    gridRow: "span 13"
  },
  cardHeader: {
    marginBottom: "40px"
  },
  cardContent: {
    justifyContent: "flex-start",
    pb: 2
  }
}
